/**
 * Retrieves the configured public server URL.
 *
 * @returns Public server URL from the environment.
 */
export const getServerSideURL = () => {
	return process.env.PUBLIC_URL;
};

/**
 * Converts a path or full URL to an absolute URL using the server origin.
 *
 * @param pathOrUrl - Relative path (e.g. "/media/foo") or absolute URL.
 * @returns Absolute URL.
 */
export function toAbsoluteUrl(pathOrUrl: string): string {
	if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
	const base = getServerSideURL();
	return pathOrUrl.startsWith("/") ? `${base}${pathOrUrl}` : `${base}/${pathOrUrl}`;
}

/**
 * Normalizes a URL to a comparable pathname.
 *
 * @param url - Relative path or absolute URL.
 * @returns Normalized pathname, or an empty string for hash-only URLs.
 */
export function getPathname(url: string): string {
	if (url === "#" || url.startsWith("#")) return "";

	const path = new URL(url, getServerSideURL() ?? "http://localhost").pathname;
	const normalizedPath = path === "/home" ? "/" : path.replace(/\/$/, "");

	return normalizedPath || "/";
}

/**
 * Constructs a full URL from slug pieces.
 *
 * @param slug - Array of URL path segments.
 * @returns Full URL starting with the current domain.
 */
export function buildUrl(...slug: string[]): string {
	const canUseDOM = !!(typeof window !== "undefined" && window.document && window.document.createElement);

	let base = getServerSideURL();
	if (canUseDOM) base = getClientSideURL();

	const path = slug.join("/");
	return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
}

/**
 * Retrieves the current browser origin or configured server URL.
 *
 * @returns Client-side origin when available, otherwise the server URL.
 */
export const getClientSideURL = () => {
	if (typeof window === "undefined") {
		return getServerSideURL() ?? "";
	}

	const protocol = window.location.protocol;
	const domain = window.location.hostname;
	const port = window.location.port;

	return `${protocol}//${domain}${port ? `:${port}` : ""}`;
};

/**
 * Normalizes and bounds the client-reported page URL for storage.
 *
 * @param raw - Raw value from the submit request body.
 * @returns A safe string or undefined if missing/invalid.
 */
export function sanitizeUrlString(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined;

	const trimmed = raw.trim().slice(0, 2048);
	if (trimmed.length === 0) return undefined;

	if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(trimmed)) return undefined;

	return trimmed;
}

/**
 * Converts a slug into a human-readable title.
 *
 * @param slug - Optional slug to prettify.
 * @returns Prettified slug or an empty string when missing.
 */
export function prettifySlug(slug?: string): string {
	if (!slug) return "";
	return slug.replace(/[-_/]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Checks whether the slug points to the home page.
 *
 * @param slug - Optional URL path segments.
 * @returns True when the slug is exactly "home".
 */
export function isHome(slug?: string[]) {
	if (!slug) return false;

	return slug.length === 1 && slug[0] === "home";
}
